/**
 * ListView — combinador responsivo tabela ↔ cards (Design System Bloco D).
 *
 * UMA fonte de dados + UMA definição de colunas governam dois renders:
 *   - desktop/tablet (> breakpoint): <DataTable> (table-layout: fixed)
 *   - mobile (<= breakpoint): <CardList> (1 row = 1 card)
 *
 * Princípio: responsividade NÃO é retrofit. A página declara a hierarquia
 * de campos UMA vez (priority/cardSlot) e o ListView decide o render.
 * Nunca dois componentes separados <TabelaDesktop> + <CardsMobile>.
 *
 * Uso:
 *   <ListView
 *     data={pedidos}
 *     rowKey={(p) => p.id}
 *     onRowClick={(p) => navigate(`/pedidos/${p.id}`)}
 *     columns={[
 *       { key: "numero", label: "Nº", priority: "primary", width: "14%" },
 *       { key: "cliente", label: "Cliente", priority: "primary", width: "26%" },
 *       { key: "situacao", label: "Situação", cardSlot: "badge",
 *         render: (p) => <Pill tone={...}>{p.situacao}</Pill>, width: "14%" },
 *       { key: "valor", label: "Valor", priority: "primary", align: "right",
 *         cardSlot: "aside", width: "14%" },
 *       { key: "criado", label: "Criado", priority: "secondary", width: "14%" },
 *       { key: "vendedor", label: "Vendedor", priority: "hidden-mobile" },
 *     ]}
 *   />
 *
 * Column shape:
 *   - key: string (obrigatório, único)
 *   - label: string (cabeçalho desktop + label do meta no card)
 *   - render?: (row, index) => node — célula custom (default: row[key])
 *   - priority?: 'primary' | 'secondary' | 'hidden-mobile' (default 'secondary')
 *   - cardSlot?: 'title' | 'aside' | 'badge' | 'meta' — override explícito
 *   - width?: string — largura desktop (ex.: "14%" | "120px")
 *   - align?: 'left' | 'right' | 'center'
 *
 * Props:
 *   - data: array
 *   - columns: array<Column>
 *   - rowKey?: (row, index) => key (default: row.id ?? index)
 *   - onRowClick?: (row) => void
 *   - loading?: bool — mostra skeleton (só desktop)
 *   - loadingRows?: number (default 6)
 *   - emptyState?: node — render quando data vazio
 *   - renderCard?: (row, index) => node — escape hatch: card totalmente custom
 *   - forceMode?: 'table' | 'cards' — força um render (ignora breakpoint)
 *   - breakpoint?: number — px abaixo do qual vira cards (default BP_MOBILE)
 */
import React from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { BP_MOBILE } from "../../styles/canonical/breakpoints";
import DataTable from "./DataTable";
import CardList from "./CardList";
import CardListCustom from "./CardListCustom";
import { EmptyWrap } from "./styles";

const defaultRowKey = (row, index) => row?.id ?? index;

export default function ListView({
  data = [],
  columns = [],
  rowKey = defaultRowKey,
  onRowClick,
  loading = false,
  loadingRows = 6,
  emptyState,
  renderCard,
  forceMode,
  breakpoint = BP_MOBILE,
}) {
  const isMobileViewport = useMediaQuery(`(max-width: ${breakpoint}px)`);
  const mode =
    forceMode || (isMobileViewport ? "cards" : "table");

  // Empty state (não aplica durante loading).
  if (!loading && (!data || data.length === 0)) {
    return (
      <EmptyWrap>
        {emptyState || "Nenhum registro encontrado."}
      </EmptyWrap>
    );
  }

  if (mode === "cards") {
    // Loading em mobile: cai pro DataTable skeleton (mais simples) só se
    // estiver carregando; senão renderiza cards.
    if (loading) {
      return (
        <DataTable
          data={[]}
          columns={columns}
          rowKey={rowKey}
          loading
          loadingRows={loadingRows}
        />
      );
    }
    if (typeof renderCard === "function") {
      return (
        <CardListCustom
          data={data}
          rowKey={rowKey}
          onRowClick={onRowClick}
          renderCard={renderCard}
        />
      );
    }
    return (
      <CardList
        data={data}
        columns={columns}
        rowKey={rowKey}
        onRowClick={onRowClick}
      />
    );
  }

  return (
    <DataTable
      data={data}
      columns={columns}
      rowKey={rowKey}
      onRowClick={onRowClick}
      loading={loading}
      loadingRows={loadingRows}
    />
  );
}

export { default as DataTable } from "./DataTable";
export { default as CardList } from "./CardList";
